// =============================================================================
// Validation Utilities
// =============================================================================

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const MIN_PASSWORD_LENGTH = 8;

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Validate email format
 */
export function validateEmail(email: string): ValidationResult {
  const errors: string[] = [];

  if (!email) {
    errors.push('Email is required');
  } else if (!EMAIL_REGEX.test(email)) {
    errors.push('Invalid email format');
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Granular result of a password-strength check. Mirrors the API's password
 * validator (apps/api/internal/validator) so clients can tell the user exactly
 * which requirement failed instead of a generic rejection.
 */
export interface PasswordStrengthResult {
  valid: boolean;
  tooShort: boolean;
  missingUpper: boolean;
  missingLower: boolean;
  missingNumber: boolean;
}

const UPPERCASE_REGEX = /\p{Lu}/u;
const LOWERCASE_REGEX = /\p{Ll}/u;
const NUMBER_REGEX = /\p{N}/u;

/**
 * Check a password against the same strength rules the API enforces:
 * minimum length plus at least one uppercase letter, one lowercase letter,
 * and one number.
 */
export function validatePasswordStrength(password: string): PasswordStrengthResult {
  const tooShort = password.length < MIN_PASSWORD_LENGTH;
  const missingUpper = !UPPERCASE_REGEX.test(password);
  const missingLower = !LOWERCASE_REGEX.test(password);
  const missingNumber = !NUMBER_REGEX.test(password);

  return {
    valid: !tooShort && !missingUpper && !missingLower && !missingNumber,
    tooShort,
    missingUpper,
    missingLower,
    missingNumber,
  };
}

/**
 * Validate password strength
 */
export function validatePassword(password: string): ValidationResult {
  const errors: string[] = [];

  if (!password) {
    errors.push('Password is required');
  } else if (password.length < MIN_PASSWORD_LENGTH) {
    errors.push(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Validate login input
 */
export function validateLoginInput(input: { email: string; password: string }): ValidationResult {
  const errors: string[] = [];

  const emailResult = validateEmail(input.email);
  const passwordResult = validatePassword(input.password);

  errors.push(...emailResult.errors, ...passwordResult.errors);

  return { valid: errors.length === 0, errors };
}

const DIGIT_REGEX = /^\d+$/;

export type IdentifierKind = 'id' | 'email';

/**
 * Validate a participant login identifier. Accepts either a sequential numeric
 * ID or a well-formed email. Returns the classification so callers can adjust
 * UI hints (e.g. label "ID" vs "Email" after focus-blur).
 */
export function validateIdentifier(input: string): ValidationResult & { kind?: IdentifierKind } {
  const trimmed = input?.trim() ?? '';

  if (!trimmed) {
    return { valid: false, errors: ['ID or email is required'] };
  }

  if (DIGIT_REGEX.test(trimmed)) {
    return { valid: true, errors: [], kind: 'id' };
  }

  const emailResult = validateEmail(trimmed);
  if (emailResult.valid) {
    return { valid: true, errors: [], kind: 'email' };
  }

  return { valid: false, errors: ['Enter a valid ID or email'] };
}

/**
 * Validate participant login input (identifier + password).
 */
export function validateParticipantLoginInput(input: {
  identifier: string;
  password: string;
}): ValidationResult {
  const identifierResult = validateIdentifier(input.identifier);
  const passwordResult = validatePassword(input.password);

  const errors = [...identifierResult.errors, ...passwordResult.errors];

  return { valid: errors.length === 0, errors };
}

const PHONE_MIN_LENGTH = 7;
const PHONE_MAX_LENGTH = 32;
const PHONE_REGEX = /^[0-9+()\s-]+$/;

/**
 * Validate a phone number. Accepts digits plus common separators (+, spaces,
 * parens, hyphens). The backend applies a stricter length check.
 */
export function validatePhone(phone: string): ValidationResult {
  const errors: string[] = [];
  const trimmed = phone?.trim() ?? '';

  if (!trimmed) {
    errors.push('Phone is required');
  } else if (trimmed.length < PHONE_MIN_LENGTH || trimmed.length > PHONE_MAX_LENGTH) {
    errors.push(`Phone must be between ${PHONE_MIN_LENGTH} and ${PHONE_MAX_LENGTH} characters`);
  } else if (!PHONE_REGEX.test(trimmed)) {
    errors.push('Phone may only contain digits and + ( ) - spaces');
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Validate registration input
 */
export function validateRegisterInput(input: {
  email: string;
  password: string;
  name: string;
  phone: string;
}): ValidationResult {
  const errors: string[] = [];

  const emailResult = validateEmail(input.email);
  const passwordResult = validatePassword(input.password);
  const phoneResult = validatePhone(input.phone);

  errors.push(...emailResult.errors, ...passwordResult.errors, ...phoneResult.errors);

  if (!input.name || input.name.trim().length === 0) {
    errors.push('Name is required');
  }

  return { valid: errors.length === 0, errors };
}
